/**
 * Surface ids stamped on every generated vertex (MeshBuilder.surface), picking the material the
 * renderer textures it with (src/render/surfaceTextures.ts). 0 = none: plain vertex colour, as before.
 * Photo surfaces map one-to-one onto the layers of the texture arrays (layer = id − 1), in the order of
 * scripts/fetch_textures.py; `terrain` blends grass, dirt and rock by slope in the shader.
 */
export const SURFACES = [
  'none',
  'asphalt',
  'asphaltWorn',
  'tar',
  'concrete',
  'concreteLight',
  'pavers',
  'grass',
  'dirt',
  'rock',
  'gravel',
  'brickRed',
  'brickYellow',
  'brickOrange',
  'stucco',
  'plaster',
  'lobbyStone',
  'tileWhite',
  'carpet',
  'wood',
  'metal',
  'terrain',
] as const;

export type SurfaceName = (typeof SURFACES)[number];

export const S = Object.fromEntries(SURFACES.map((n, i) => [n, i])) as Record<SurfaceName, number>;

/** Photo surfaces: those with a texture layer (all but none and terrain). */
export const PHOTO_SURFACES = SURFACES.filter((n) => n !== 'none' && n !== 'terrain') as Exclude<SurfaceName, 'none' | 'terrain'>[];

/** Metres per texture repeat (a power of two, so repeats line up across tiles; see the shader). */
export const SURFACE_METRES: Record<(typeof PHOTO_SURFACES)[number], number> = {
  asphalt: 4,
  asphaltWorn: 4,
  tar: 4,
  concrete: 4,
  concreteLight: 4,
  pavers: 2,
  grass: 4,
  dirt: 4,
  rock: 8,
  gravel: 2,
  brickRed: 2,
  brickYellow: 2,
  brickOrange: 2,
  stucco: 4,
  plaster: 4,
  lobbyStone: 2,
  tileWhite: 1,
  carpet: 2,
  wood: 2,
  metal: 2,
};

/**
 * Mean albedo of each photo texture (public/textures/manifest.json; a test keeps them in sync). A vertex
 * coloured with its surface's mean shows the photo's own colours; generators scale it for variety.
 */
export const SURFACE_MEAN: Record<(typeof PHOTO_SURFACES)[number], [number, number, number]> = {
  asphalt: [0.224, 0.224, 0.216],
  asphaltWorn: [0.451, 0.447, 0.447],
  tar: [0.302, 0.29, 0.251],
  concrete: [0.369, 0.353, 0.341],
  concreteLight: [0.722, 0.722, 0.722],
  pavers: [0.6, 0.58, 0.529],
  grass: [0.341, 0.384, 0.169],
  dirt: [0.412, 0.349, 0.271],
  rock: [0.435, 0.423, 0.361],
  gravel: [0.392, 0.396, 0.38],
  brickRed: [0.494, 0.392, 0.306],
  brickYellow: [0.698, 0.561, 0.404],
  brickOrange: [0.616, 0.427, 0.333],
  stucco: [0.722, 0.718, 0.69],
  plaster: [0.843, 0.828, 0.816],
  lobbyStone: [0.851, 0.706, 0.553],
  tileWhite: [0.969, 0.969, 0.969],
  carpet: [0.169, 0.188, 0.231],
  wood: [0.604, 0.478, 0.365],
  metal: [0.318, 0.337, 0.361],
};

/** `s`'s mean albedo scaled by `k` (a per-building variation). */
export const meanColor = (s: (typeof PHOTO_SURFACES)[number], k = 1): [number, number, number] => SURFACE_MEAN[s].map((c) => Math.min(1, c * k)) as [number, number, number];

/**
 * Roughness range per surface: the maps' values are remapped into [min, max]. The photo maps run glossy
 * (grass averages 0.26), which against the sky's environment light turned lawns and asphalt into sheen.
 */
export const SURFACE_ROUGHNESS: Record<(typeof PHOTO_SURFACES)[number], [number, number]> = {
  asphalt: [0.75, 1],
  asphaltWorn: [0.75, 1],
  tar: [0.7, 1],
  concrete: [0.75, 1],
  concreteLight: [0.7, 1],
  pavers: [0.7, 1],
  grass: [0.85, 1],
  dirt: [0.85, 1],
  rock: [0.75, 1],
  gravel: [0.85, 1],
  brickRed: [0.75, 1],
  brickYellow: [0.75, 1],
  brickOrange: [0.75, 1],
  stucco: [0.8, 1],
  plaster: [0.8, 1],
  lobbyStone: [0.35, 0.8],
  tileWhite: [0.2, 0.6],
  carpet: [0.9, 1],
  wood: [0.4, 0.85],
  metal: [0.3, 0.7],
};

/** Wall surfaces of row-house units, picked per unit. */
export const LOWRISE_WALLS = ['brickRed', 'brickOrange', 'brickYellow', 'stucco', 'brickRed'] as const;
